import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES, setLocale, type AppLocale } from '../i18n';
import { updateLocale } from '../lib/auth';
import { selectIsAuthenticated, useAuthStore } from '../stores/auth';

const LABELS: Record<AppLocale, string> = {
  en: 'English',
  es: 'Español',
};

/** Short labels for phones, where the header has no room for full names. */
const SHORT_LABELS: Record<AppLocale, string> = {
  en: 'EN',
  es: 'ES',
};

function useWideScreen(): boolean {
  const query = '(min-width: 640px)';
  const [wide, setWide] = useState(() => typeof window === 'undefined' || window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = (): void => setWide(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return wide;
}

/** Compact language picker for the header. Persists via the i18n detector. */
export function LanguageSwitcher(): JSX.Element {
  const { t, i18n } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const wide = useWideScreen();
  const current = (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as AppLocale)
    : 'en';

  return (
    <label className="flex items-center gap-1.5 text-sm">
      <span className="sr-only">{t('nav.languageLabel')}</span>
      <span aria-hidden="true" className="hidden text-white/70 md:inline">
        {t('nav.language')}
      </span>
      <select
        aria-label={t('nav.languageLabel')}
        value={current}
        onChange={(e) => {
          const next = e.target.value as AppLocale;
          try {
            localStorage.setItem(LOCALE_STORAGE_KEY, next);
          } catch {
            /* private mode — the detector just won't persist */
          }
          void setLocale(next);
          // Keep the account preference (and future auth emails) in sync.
          // Fire-and-forget: the switcher must never block on the network.
          if (isAuthenticated) {
            void updateLocale(next).catch(() => {
              /* offline or session issue — the stored localStorage choice still applies */
            });
          }
        }}
        className="rounded-md bg-white/15 px-2 py-1.5 text-sm font-medium text-white hover:bg-white/25 focus-visible:outline-white"
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale} value={locale} className="text-slate-900">
            {wide ? LABELS[locale] : SHORT_LABELS[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
